// Configurações › E-mail de saída: caixa do vendedor (SMTP/IMAP) e regras da empresa — specs/15-email.md §3.
import { PRESETS_PROVEDOR, PROVEDORES_EMAIL, PORTAS_SMTP, type Canal, type ProvedorEmail } from '@crm/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Progresso, Toast, useToast } from '../components/ui';
import { dataHora } from '../format';
import { PreviaAssinatura, subirImagemEmail } from './EditorEmail';
import { EmpresaConfig } from './EmpresaConfig';
import { StatusClientes } from './StatusClientes';
import { NegociacaoConfig } from './NegociacaoConfig';
import { porGrupo, useStatusClientes } from '../status';

export interface Caixa {
  provedor: ProvedorEmail; nomeRemetente: string; email: string; smtpHost: string; smtpPorta: number; smtpSeguranca: 'SSL' | 'STARTTLS';
  imapHost: string | null; imapPorta: number | null; usuario: string; assinatura: string | null; lerRespostas: boolean; temSenha: boolean;
  /** Imagem da assinatura (specs/25) */
  assinaturaImagemId: string | null; assinaturaImagemUrl: string | null; assinaturaImagemLargura: number; assinaturaImagemPosicao: 'LADO' | 'ACIMA';
  status: 'NAO_TESTADA' | 'OK' | 'FALHA'; ultimoErro: string | null; verificadaEm: string | null; lidaEm: string | null;
}
export interface MinhaCaixa { caixa: Caixa | null; enviadosHoje: number; limiteDiario: number }
interface ConfigEmail {
  canalPadrao: Canal; limiteDiario: number; limiteHora: number; registrarRespostas: boolean; retornoResposta: boolean; servidorPronto: boolean;
  /** E-mail avulso: clique no botão WhatsApp leva a este status (specs/24) */
  whatsappStatusId: string | null; whatsappProtegeAtivo: boolean;
}

export const useMinhaCaixa = () => useQuery({ queryKey: ['minha-caixa'], queryFn: () => api<MinhaCaixa>('/email/minha-caixa'), staleTime: 30_000 });

export const STATUS_CAIXA: Record<Caixa['status'], { label: string; cls: string }> = {
  OK: { label: 'Conectada', cls: 'green' },
  NAO_TESTADA: { label: 'Não testada', cls: 'amber' },
  FALHA: { label: 'Com falha', cls: 'red' },
};

const SECOES = [{ id: 'empresa', label: 'Empresa' }, { id: 'email', label: 'E-mail de saída' }, { id: 'metas', label: 'Metas' }, { id: 'status', label: 'Status de clientes' }, { id: 'negociacao', label: 'Negociação' }] as const;

export function ConfiguracoesPage() {
  const { secao } = useParams<{ secao: string }>();
  return (
    <>
      <h1>Configurações</h1>
      <nav className="tabs" aria-label="Seções de configuração">
        {SECOES.map((s) => <NavLink key={s.id} to={`/configuracoes/${s.id}`} className="tab">{s.label}</NavLink>)}
      </nav>
      {secao === 'empresa' ? <EmpresaConfig /> : secao === 'email' ? <EmailSaida /> : secao === 'metas' ? <Metas /> : secao === 'status' ? <StatusClientes /> : secao === 'negociacao' ? <NegociacaoConfig /> : <div className="empty">Seção inexistente.</div>}
    </>
  );
}

function EmailSaida() {
  const { pode } = useAuth();
  const admin = pode('CONFIGURACOES_GERENCIAR');
  const [aba, setAba] = useState<'minha' | 'empresa'>('minha');
  const cfg = useQuery({ queryKey: ['email-config'], queryFn: () => api<ConfigEmail>('/email/config') });
  return (
    <div className="col" style={{ gap: 18, maxWidth: 1080 }}>
      <div className="col" style={{ gap: 4 }}>
        <h2 style={{ fontSize: 18 }}>E-mail de saída</h2>
        <span className="muted">Cada vendedor envia pela própria caixa de e-mail. A resposta do cliente chega na caixa dele e também aparece na ficha do cliente.</span>
      </div>
      {cfg.data && !cfg.data.servidorPronto && (
        <div className="alert warn"><Icon name="alerta" /><span>O servidor ainda não tem a chave de e-mail (EMAIL_CRYPTO_KEY). Peça ao responsável técnico para configurá-la antes de cadastrar as caixas.</span></div>
      )}
      {admin && (
        <div className="seg" role="tablist" aria-label="Escopo">
          <button type="button" role="tab" aria-selected={aba === 'minha'} onClick={() => setAba('minha')}>Minha caixa</button>
          <button type="button" role="tab" aria-selected={aba === 'empresa'} onClick={() => setAba('empresa')}>Empresa · administrador</button>
        </div>
      )}
      {aba === 'minha' || !admin ? <MinhaCaixaForm /> : <Empresa />}
    </div>
  );
}

// ----------------------------------------------------------------------------- minha caixa

interface Form {
  provedor: ProvedorEmail; nomeRemetente: string; email: string; usuario: string; senha: string; smtpHost: string; smtpPorta: number;
  smtpSeguranca: 'SSL' | 'STARTTLS'; lerRespostas: boolean; imapHost: string; assinatura: string;
}

function MinhaCaixaForm() {
  const q = useMinhaCaixa();
  if (q.isLoading) return <Carregando />;
  if (q.error) return <Erro erro={mensagemErro(q.error)} />;
  return <MinhaCaixaEditor dados={q.data!} />;
}

function MinhaCaixaEditor({ dados }: { dados: MinhaCaixa }) {
  const { me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const c = dados.caixa;
  const [f, setF] = useState<Form>(() => c
    ? { provedor: c.provedor, nomeRemetente: c.nomeRemetente, email: c.email, usuario: c.usuario, senha: '', smtpHost: c.smtpHost, smtpPorta: c.smtpPorta,
        smtpSeguranca: c.smtpSeguranca, lerRespostas: c.lerRespostas, imapHost: c.imapHost ?? '', assinatura: c.assinatura ?? '' }
    : { provedor: 'OUTRO', nomeRemetente: `${me?.usuario.nome ?? ''} · ${me?.empresa.nome ?? ''}`.slice(0, 150), email: me?.usuario.email ?? '',
        usuario: me?.usuario.email ?? '', senha: '', smtpHost: '', smtpPorta: 587, smtpSeguranca: 'STARTTLS', lerRespostas: true, imapHost: '',
        assinatura: `${me?.usuario.nome ?? ''}\n${me?.empresa.nome ?? ''}` });
  const [usuarioTocado, setUsuarioTocado] = useState(!!c && c.usuario !== c.email);
  const [erro, setErro] = useState<string | null>(null);
  const [checks, setChecks] = useState<string[] | null>(null);
  const [ocupado, setOcupado] = useState<null | 'salvar' | 'remover'>(null);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const preset = PRESETS_PROVEDOR[f.provedor];

  const escolherProvedor = (p: ProvedorEmail) => {
    const pr = PRESETS_PROVEDOR[p];
    setF((x) => ({ ...x, provedor: p, smtpHost: pr.smtpHost || (p === 'OUTRO' ? x.smtpHost : ''), smtpPorta: pr.smtpPorta, smtpSeguranca: pr.smtpSeguranca, imapHost: pr.imapHost || (p === 'OUTRO' ? x.imapHost : '') }));
  };

  async function salvarETestar() {
    setErro(null);
    setChecks(null);
    setOcupado('salvar');
    try {
      await api('/email/minha-caixa', {
        method: 'PUT',
        body: {
          provedor: f.provedor, nomeRemetente: f.nomeRemetente, email: f.email, usuario: f.usuario, smtpHost: f.smtpHost, smtpPorta: f.smtpPorta,
          smtpSeguranca: f.smtpSeguranca, lerRespostas: f.lerRespostas, imapHost: f.lerRespostas ? f.imapHost : null, imapPorta: f.lerRespostas ? 993 : null,
          assinatura: f.assinatura, ...(f.senha ? { senha: f.senha } : {}),
        },
      });
      try {
        const r = await api<{ checks: string[] }>('/email/minha-caixa/testar', { method: 'POST' });
        setChecks(r.checks);
        set('senha', '');
        toast.show('Caixa conectada.');
      } catch (e) {
        setErro(`Salvo, mas o teste falhou: ${mensagemErro(e)}`);
      }
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setOcupado(null);
      await qc.invalidateQueries({ queryKey: ['minha-caixa'] });
    }
  }

  async function remover() {
    setOcupado('remover');
    try {
      await api('/email/minha-caixa', { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: ['minha-caixa'] });
      toast.show('Caixa removida.');
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setOcupado(null);
    }
  }

  const status = c ? STATUS_CAIXA[c.status] : { label: 'Não configurada', cls: 'amber' };
  const completo = f.nomeRemetente.trim() && f.email.trim() && f.usuario.trim() && f.smtpHost.trim() && (c?.temSenha || f.senha) && (!f.lerRespostas || f.imapHost.trim());

  return (
    <div className="row" style={{ gap: 20 }}>
      <section className="card" style={{ flex: '1.5 1 440px' }} aria-labelledby="h-caixa">
        <div className="card-head"><h2 id="h-caixa">Minha caixa de e-mail</h2><span className={`badge ${status.cls}`}>{status.label}</span></div>
        <Erro erro={erro} />
        {c?.status === 'FALHA' && c.ultimoErro && !erro && <div className="alert danger" role="alert"><Icon name="alerta" /><span>{c.ultimoErro}</span></div>}

        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <label className="field"><span>Nome do remetente</span><input className="input" value={f.nomeRemetente} maxLength={150} onChange={(e) => set('nomeRemetente', e.target.value)} /></label>
          <label className="field"><span>E-mail</span>
            <input className="input num" type="email" value={f.email} onChange={(e) => { set('email', e.target.value); if (!usuarioTocado) set('usuario', e.target.value); }} />
          </label>
        </div>

        <div className="field">
          <span>Provedor</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="group" aria-label="Provedor de e-mail">
            {PROVEDORES_EMAIL.map((p) => (
              <button key={p} type="button" className={`pill${f.provedor === p ? ' on' : ''}`} aria-pressed={f.provedor === p} onClick={() => escolherProvedor(p)}>{PRESETS_PROVEDOR[p].nome}</button>
            ))}
          </div>
          <span className="hint">{preset.dica}</span>
        </div>

        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <label className="field"><span>Usuário</span><input className="input num" value={f.usuario} onChange={(e) => { setUsuarioTocado(true); set('usuario', e.target.value); }} /></label>
          <label className="field"><span>Senha{f.provedor === 'GOOGLE' ? ' de app' : ''}</span>
            <input className="input" type="password" autoComplete="new-password" value={f.senha} placeholder={c?.temSenha ? '•••••••• (mantida)' : ''} onChange={(e) => set('senha', e.target.value)} />
          </label>
        </div>

        <div className="form-grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(110px, 1fr) minmax(130px, 1fr)' }}>
          <label className="field"><span>Servidor SMTP (envio)</span><input className="input num" value={f.smtpHost} placeholder="smtp.seudominio.com.br" onChange={(e) => set('smtpHost', e.target.value.trim())} /></label>
          <label className="field"><span>Porta</span>
            <select className="select num" value={f.smtpPorta} onChange={(e) => { const p = Number(e.target.value); setF((x) => ({ ...x, smtpPorta: p, smtpSeguranca: p === 465 ? 'SSL' : 'STARTTLS' })); }}>
              {PORTAS_SMTP.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="field"><span>Segurança</span>
            <select className="select" value={f.smtpSeguranca} onChange={(e) => set('smtpSeguranca', e.target.value as 'SSL' | 'STARTTLS')}>
              <option value="SSL">SSL/TLS</option><option value="STARTTLS">STARTTLS</option>
            </select>
          </label>
        </div>

        <label className="check"><input type="checkbox" checked={f.lerRespostas} onChange={(e) => set('lerRespostas', e.target.checked)} />Registrar as respostas dos clientes na ficha (IMAP)</label>
        {f.lerRespostas && (
          <div className="form-grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(110px, 1fr)' }}>
            <label className="field"><span>Servidor IMAP (respostas)</span><input className="input num" value={f.imapHost} placeholder="imap.seudominio.com.br" onChange={(e) => set('imapHost', e.target.value.trim())} /></label>
            <label className="field"><span>Porta</span><input className="input num" value="993 (SSL)" disabled /></label>
          </div>
        )}
        <span className="small faint">O CRM só lê mensagens de remetentes que estão na carteira e não marca nada como lido.</span>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn primary" disabled={!completo || !!ocupado} onClick={salvarETestar}>{ocupado === 'salvar' ? 'Testando conexão…' : 'Salvar e testar conexão'}</button>
          {c && <button className="btn danger" disabled={!!ocupado} onClick={remover}>Remover caixa</button>}
          <span className="small muted">Envia um e-mail de teste para você mesmo.</span>
        </div>
        {checks && (
          <div className="status-lista" role="status">
            {checks.map((k) => <span key={k}><Icon name="check" size={14} />{k}</span>)}
          </div>
        )}
        {c?.verificadaEm && <span className="small faint">Último teste: {dataHora(c.verificadaEm)}{c.lidaEm ? ` · respostas lidas pela última vez ${dataHora(c.lidaEm)}` : ''}</span>}
      </section>

      <section className="card" style={{ flex: '1 1 340px' }} aria-labelledby="h-ass">
        <h2 id="h-ass">Assinatura</h2>
        <CamposAssinatura caixa={c} texto={f.assinatura} onTexto={(v) => set('assinatura', v)} onSalvo={(m) => toast.show(m)} />
        <div className="col" style={{ gap: 6, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <span className="small muted">Enviados hoje</span>
          <Progresso valor={dados.enviadosHoje} total={dados.limiteDiario} />
          <span className="small muted num">{dados.enviadosHoje} de {dados.limiteDiario} (limite diário da empresa)</span>
        </div>
      </section>
      <Toast msg={toast.msg} onDone={toast.clear} />
    </div>
  );
}

const TAMANHOS_ASSIN = [{ v: 120, nome: 'Pequena' }, { v: 180, nome: 'Média' }, { v: 240, nome: 'Grande' }] as const;

/** Assinatura de cada vendedor: texto + imagem (logo, cartão), tamanho e posição — specs/25. */
function CamposAssinatura({ caixa, texto, onTexto, onSalvo }: {
  caixa: Caixa | null; texto: string; onTexto: (v: string) => void; onSalvo: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [img, setImg] = useState<{ id: string; url: string } | null>(caixa?.assinaturaImagemId && caixa.assinaturaImagemUrl ? { id: caixa.assinaturaImagemId, url: caixa.assinaturaImagemUrl } : null);
  const [largura, setLargura] = useState<number>(caixa?.assinaturaImagemLargura ?? 180);
  const [posicao, setPosicao] = useState<'LADO' | 'ACIMA'>(caixa?.assinaturaImagemPosicao ?? 'LADO');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<null | 'imagem' | 'salvar'>(null);
  const mudou = !!caixa && (texto !== (caixa.assinatura ?? '') || (img?.id ?? null) !== caixa.assinaturaImagemId
    || largura !== caixa.assinaturaImagemLargura || posicao !== caixa.assinaturaImagemPosicao);

  async function escolher(f: File | undefined) {
    if (!f) return;
    setErro(null);
    setOcupado('imagem');
    try {
      const r = await subirImagemEmail(f, 480);
      setImg({ id: r.id, url: r.url });
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setOcupado(null);
    }
  }

  async function salvar() {
    setErro(null);
    setOcupado('salvar');
    try {
      await api('/email/minha-caixa/assinatura', { method: 'PUT', body: { assinatura: texto.trim() || null, imagemId: img?.id ?? null, largura, posicao } });
      await qc.invalidateQueries({ queryKey: ['minha-caixa'] });
      onSalvo('Assinatura salva.');
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setOcupado(null);
    }
  }

  return (
    <>
      <div className="field">
        <span>Imagem (logo, cartão de visita ou foto)</span>
        <div className="assin-drop">
          {img ? <img src={img.url} width={Math.min(largura, 160)} alt="Imagem da assinatura" /> : <span className="assin-vazia">sem imagem</span>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <label className={`btn sm${!caixa || ocupado ? ' disabled' : ''}`} aria-disabled={!caixa || !!ocupado}>
              {ocupado === 'imagem' ? 'Enviando…' : img ? 'Trocar' : 'Escolher imagem'}
              <input type="file" accept="image/png,image/jpeg,image/webp" hidden disabled={!caixa || !!ocupado} onChange={(e) => { void escolher(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
            {img && <button type="button" className="btn sm" disabled={!!ocupado} onClick={() => setImg(null)}>Remover</button>}
          </div>
        </div>
        <span className="hint">{caixa ? 'PNG ou JPG, até 1 MB. Vai embutida no e-mail.' : 'Salve a caixa primeiro para adicionar a imagem.'}</span>
      </div>
      {img && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          <div className="field"><span>Tamanho</span>
            <div className="seg" role="group" aria-label="Tamanho da imagem">
              {TAMANHOS_ASSIN.map((t) => <button key={t.v} type="button" className={largura === t.v ? 'on' : ''} aria-pressed={largura === t.v} onClick={() => setLargura(t.v)}>{t.nome}</button>)}
            </div>
          </div>
          <div className="field"><span>Posição</span>
            <div className="seg" role="group" aria-label="Posição da imagem">
              {([['LADO', 'Ao lado do texto'], ['ACIMA', 'Acima do texto']] as const).map(([v, nome]) => (
                <button key={v} type="button" className={posicao === v ? 'on' : ''} aria-pressed={posicao === v} onClick={() => setPosicao(v)}>{nome}</button>
              ))}
            </div>
          </div>
        </div>
      )}
      <label className="field"><span>Texto</span>
        <textarea className="textarea" rows={4} maxLength={2000} value={texto} onChange={(e) => onTexto(e.target.value)} aria-label="Texto da assinatura" />
      </label>
      {(texto.trim() || img) && (
        <div className="field"><span>Como fica no e-mail</span>
          <div className="previa-email html" style={{ padding: '12px 16px' }}>
            <p style={{ margin: 0, color: '#8a8f98' }}>… texto do e-mail …</p>
            <PreviaAssinatura texto={texto.trim()} imagemUrl={img?.url} largura={largura} posicao={posicao} />
          </div>
        </div>
      )}
      <Erro erro={erro} />
      {caixa && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn primary" disabled={!mudou || !!ocupado} onClick={salvar}>{ocupado === 'salvar' ? 'Salvando…' : 'Salvar assinatura'}</button>
        </div>
      )}
      <span className="small faint">Vai no fim de todo e-mail enviado pelo CRM (ficha e campanhas). O rodapé de descadastro (LGPD) é incluído automaticamente.</span>
    </>
  );
}

// ----------------------------------------------------------------------------- metas

interface MetasResp { metaContatosDia: number; vendedores: Array<{ usuarioId: string; nome: string; perfil: string; metaContatosDia: number | null }> }

function Metas() {
  const { pode } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const admin = pode('CONFIGURACOES_GERENCIAR');
  const q = useQuery({ queryKey: ['metas'], queryFn: () => api<MetasResp>('/config/metas') });
  const [padrao, setPadrao] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  if (q.isLoading) return <Carregando />;
  if (q.error) return <Erro erro={mensagemErro(q.error)} />;
  const d = q.data!;
  const atual = padrao ?? String(d.metaContatosDia);
  const recarregar = () => Promise.all([qc.invalidateQueries({ queryKey: ['metas'] }), qc.invalidateQueries({ queryKey: ['equipe'] })]);

  async function salvarPadrao() {
    setErro(null);
    try {
      await api('/config/metas', { method: 'PUT', body: { metaContatosDia: Number(atual) } });
      setPadrao(null);
      await recarregar();
      toast.show('Meta da empresa salva.');
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }
  async function salvarVendedor(usuarioId: string, valor: string) {
    setErro(null);
    try {
      await api(`/config/metas/${usuarioId}`, { method: 'PUT', body: { metaContatosDia: valor.trim() ? Number(valor) : null } });
      setEdits((x) => { const n = { ...x }; delete n[usuarioId]; return n; });
      await recarregar();
      toast.show(valor.trim() ? 'Meta do vendedor salva.' : 'Vendedor voltou a usar a meta da empresa.');
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  return (
    <div className="row" style={{ gap: 20 }}>
      <section className="card" style={{ flex: '1 1 320px', maxWidth: 460 }} aria-labelledby="h-metas">
        <h2 id="h-metas" style={{ fontSize: 18 }}>Meta da empresa</h2>
        <Erro erro={erro} />
        <label className="field"><span>Contatos por vendedor por dia</span>
          <input className="input num" type="number" min={1} max={1000} value={atual} disabled={!admin} onChange={(e) => setPadrao(e.target.value)} style={{ maxWidth: 160 }} />
          <span className="hint">Vale para quem não tem meta própria. Conta WhatsApp, e-mail, ligação e visita registrados. Nos 7 dias do dashboard, a meta é multiplicada por 7.</span>
        </label>
        {admin ? <div><button className="btn primary" disabled={padrao === null || !Number(atual)} onClick={salvarPadrao}>Salvar meta da empresa</button></div>
          : <span className="small muted">Só o administrador altera as metas.</span>}
      </section>

      <section className="card flush" style={{ flex: '1.4 1 460px' }} aria-labelledby="h-metas-v">
        <div className="card-head" style={{ padding: '16px 18px 0' }}><h2 id="h-metas-v">{admin ? 'Meta por vendedor' : 'Sua meta'}</h2></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Usuário</th><th>Contatos por dia</th>{admin && <th />}</tr></thead>
            <tbody>
              {d.vendedores.map((v) => {
                const editado = edits[v.usuarioId];
                const valor = editado ?? (v.metaContatosDia != null ? String(v.metaContatosDia) : '');
                return (
                  <tr key={v.usuarioId}>
                    <td>{v.nome}{v.perfil === 'ADMIN' && <span className="small faint"> · administrador</span>}</td>
                    <td>
                      {admin ? (
                        <input className="input num" type="number" min={1} max={1000} style={{ width: 120 }} value={valor}
                          placeholder={String(d.metaContatosDia)} title="Em branco = meta da empresa" aria-label={`Meta de ${v.nome}`}
                          onChange={(e) => setEdits((x) => ({ ...x, [v.usuarioId]: e.target.value }))} />
                      ) : <span className="num">{v.metaContatosDia ?? d.metaContatosDia}</span>}
                    </td>
                    {admin && (
                      <td>
                        {editado !== undefined && <button className="btn sm primary" onClick={() => void salvarVendedor(v.usuarioId, editado)}>Salvar</button>}
                        {editado === undefined && v.metaContatosDia != null && <button className="btn sm" onClick={() => void salvarVendedor(v.usuarioId, '')}>Usar a da empresa</button>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <span className="small faint" style={{ padding: '0 18px 16px' }}>Em branco = segue a meta da empresa.</span>
      </section>
      <Toast msg={toast.msg} onDone={toast.clear} />
    </div>
  );
}

// ----------------------------------------------------------------------------- empresa (admin)

interface CaixaVendedor {
  usuarioId: string; nome: string; perfil: string; email: string | null; provedor: ProvedorEmail | null; status: Caixa['status'] | null;
  ultimoErro: string | null; verificadaEm: string | null; lidaEm: string | null; lerRespostas: boolean; enviadosHoje: number;
}

function Empresa() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery({ queryKey: ['email-config'], queryFn: () => api<ConfigEmail>('/email/config') });
  const caixas = useQuery({ queryKey: ['email-caixas'], queryFn: () => api<CaixaVendedor[]>('/email/caixas') });
  const statusLista = useStatusClientes();
  const [f, setF] = useState<Partial<ConfigEmail>>({});
  const [erro, setErro] = useState<string | null>(null);
  if (cfg.isLoading) return <Carregando />;
  const v = { ...cfg.data!, ...f };

  async function salvar() {
    setErro(null);
    try {
      await api('/email/config', { method: 'PUT', body: { canalPadrao: v.canalPadrao, limiteDiario: Number(v.limiteDiario), limiteHora: Number(v.limiteHora), registrarRespostas: v.registrarRespostas, retornoResposta: v.retornoResposta, whatsappStatusId: v.whatsappStatusId || null, whatsappProtegeAtivo: v.whatsappProtegeAtivo } });
      setF({});
      await qc.invalidateQueries({ queryKey: ['email-config'] });
      await qc.invalidateQueries({ queryKey: ['minha-caixa'] });
      toast.show('Configuração salva.');
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  return (
    <div className="row" style={{ gap: 20 }}>
      <section className="card" style={{ flex: '1 1 340px' }} aria-labelledby="h-regras">
        <h2 id="h-regras">Regras</h2>
        <Erro erro={erro} />
        <div className="field"><span>Canal padrão (clientes sem preferência)</span>
          <div className="seg" role="group" aria-label="Canal padrão">
            {(['WHATSAPP', 'EMAIL'] as const).map((c) => <button key={c} type="button" aria-pressed={v.canalPadrao === c} onClick={() => setF({ ...f, canalPadrao: c })}>{c === 'EMAIL' ? 'E-mail' : 'WhatsApp'}</button>)}
          </div>
        </div>
        <label className="field"><span>Limite de e-mails por vendedor por dia</span>
          <input className="input num" type="number" min={1} max={2000} value={v.limiteDiario} onChange={(e) => setF({ ...f, limiteDiario: Number(e.target.value) })} style={{ maxWidth: 160 }} />
          <span className="hint">Protege a reputação das caixas: provedores bloqueiam quem envia demais.</span>
        </label>
        <label className="field"><span>Campanhas: e-mails por vendedor por hora</span>
          <input className="input num" type="number" min={1} max={1000} value={v.limiteHora} onChange={(e) => setF({ ...f, limiteHora: Number(e.target.value) })} style={{ maxWidth: 160 }} />
          <span className="hint">Ritmo do envio automático das campanhas por e-mail (o limite diário também vale).</span>
        </label>
        <label className="check"><input type="checkbox" checked={v.registrarRespostas} onChange={(e) => setF({ ...f, registrarRespostas: e.target.checked })} />Registrar respostas dos clientes na ficha</label>
        <label className="check"><input type="checkbox" checked={v.retornoResposta} onChange={(e) => setF({ ...f, retornoResposta: e.target.checked })} />Criar retorno “Responder e-mail” para o vendedor quando o cliente responder</label>
        <label className="field"><span>E-mail pela ficha: quem clicar em “Falar no WhatsApp” vai para o status</span>
          <select className="select" value={v.whatsappStatusId ?? ''} onChange={(e) => setF({ ...f, whatsappStatusId: e.target.value || null })}>
            <option value="">Não alterar o status</option>
            {porGrupo(statusLista.data).filter((g) => g.itens.length).map((g) => (
              <optgroup key={g.grupo} label={g.nome}>{g.itens.map((st) => <option key={st.id} value={st.id}>{st.nome}</option>)}</optgroup>
            ))}
          </select>
          <span className="hint">Nas campanhas, isso é escolhido em cada campanha.</span>
        </label>
        {v.whatsappStatusId && (
          <label className="check"><input type="checkbox" checked={v.whatsappProtegeAtivo} onChange={(e) => setF({ ...f, whatsappProtegeAtivo: e.target.checked })} />Não mudar quem já é cliente ativo</label>
        )}
        <span className="small faint">Todo e-mail sai com link de descadastro (LGPD). Quem se descadastra deixa de receber e-mails; o WhatsApp continua liberado. Cliques no botão “Falar no WhatsApp” são registrados: informe isso na política de privacidade.</span>
        <div><button className="btn primary" disabled={!Object.keys(f).length} onClick={salvar}>Salvar regras</button></div>
      </section>

      <section className="card flush" style={{ flex: '1.4 1 460px' }} aria-labelledby="h-caixas">
        <div className="card-head" style={{ padding: '16px 18px 0' }}><h2 id="h-caixas">Caixas dos vendedores</h2></div>
        {caixas.isLoading ? <Carregando /> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Usuário</th><th>E-mail de saída</th><th>Status</th><th>Hoje</th></tr></thead>
              <tbody>
                {caixas.data?.map((x) => {
                  const st = x.status ? STATUS_CAIXA[x.status] : { label: 'Não configurada', cls: 'amber' };
                  return (
                    <tr key={x.usuarioId}>
                      <td>{x.nome}</td>
                      <td className="num">{x.email ?? '—'}</td>
                      <td><span className={`badge ${st.cls}`} title={x.ultimoErro ?? undefined}>{st.label}</span>{x.status === 'OK' && !x.lerRespostas && <span className="small faint"> · sem respostas</span>}</td>
                      <td className="num">{x.enviadosHoje}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <span className="small faint" style={{ padding: '0 18px 16px' }}>Quem não tem caixa conectada vê o botão de e-mail com o aviso para configurar em Configurações.</span>
      </section>
      <Toast msg={toast.msg} onDone={toast.clear} />
    </div>
  );
}
